import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { ArrowUpRight, CheckCircle2, GraduationCap, LifeBuoy, X } from "lucide-react";
import type { AuthSessionResponse } from "@yagni/shared";
import { ConversationPanel } from "./ConversationPanel.js";
import {
  ConversationSidebar,
  ConversationSidebarBanner,
  IconButton,
} from "../ui/index.js";

/**
 * Native feedback side panel.
 *
 * A fixed right-edge slide-over — the same chrome as the shell's @yagni chat
 * drawer (rule border, paper-card backdrop, spring entrance) so it reads as
 * "another right panel" rather than foreign UI. Sits above the in-stage
 * Triptych rail while open. Hosts a ConversationPanel bound to parentType="feedback"
 * and parentId=session.user.id, so each user gets one persistent feedback
 * conversation per workspace (upsert key is customer_id + parent_type + parent_id).
 *
 * On conversation-ready (i.e. when the ConversationPanel has resolved the
 * upsert and knows the conversation id), POSTs /api/feedback/submit with a
 * fresh snapshot of route + browser context so God Mode has enough info for
 * triage. The backing row is created atomically server-side on the first
 * human message, then becomes visible to the team only when @yagni confirms
 * capture. This POST is a debug_context enrichment, not the critical path
 * for inbox visibility. Subsequent opens resume the same
 * conversation with full history (including any "Jack from YAGNI" replies
 * posted from God Mode); the enrichment POST is idempotent via the server's
 * ON CONFLICT DO UPDATE, so re-sends just refresh debug_context.
 */
export interface FeedbackSidePanelProps {
  session: AuthSessionResponse;
  onClose: () => void;
}

interface FeedbackDebugContext {
  route: string;
  userAgent: string;
  appVersion: string;
  workspaceSlug: string;
  workspaceId: string;
  viewport: { width: number; height: number };
  language: string;
}

/**
 * Snapshot current browser context when the conversation resolves (typically
 * within a few ms of panel open). Intentionally NOT memoized — we capture
 * values fresh each time so route / viewport reflect what the user was
 * actually looking at when they opened the panel. In practice this matches
 * "when they hit the problem": users open the feedback panel while still
 * sitting on the page that caused it and type their report without
 * navigating away.
 */
function captureDebugContext(
  session: AuthSessionResponse,
  pathname: string,
  search: string,
): FeedbackDebugContext {
  return {
    route: pathname + search,
    userAgent: navigator.userAgent,
    appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev",
    workspaceSlug: session.workspace.slug,
    workspaceId: session.workspace.id,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    language: navigator.language,
  };
}

async function submitFeedbackReport(
  conversationId: string,
  debugContext: FeedbackDebugContext,
): Promise<void> {
  // The backing row is created atomically on the backend when the first human
  // message lands and promoted after @yagni confirms capture. This POST is strictly a
  // debug_context enrichment — if it fails, the YAGNI team still sees
  // the report, just with an empty debug_context. We still check res.ok
  // and surface non-2xx responses to the console so silent backend
  // regressions don't hide.
  try {
    const res = await fetch("/api/feedback/submit", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ conversationId, debugContext }),
    });
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn(
        `Feedback debug_context enrichment failed (${res.status}) — inbox row still created server-side.`,
      );
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Feedback debug_context enrichment failed", err);
  }
}

interface MyTicketResponse {
  ticket: {
    id: string;
    status: "new" | "triaged" | "resolved";
    updatedAt: string;
  } | null;
}

/** The marketing-site Academy — self-serve lessons for "just want more help". */
const ACADEMY_URL = "https://yagni.app/academy";

export function FeedbackSidePanel({ session, onClose }: FeedbackSidePanelProps) {
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  // Track whether the user's own feedback ticket is currently resolved so
  // we can show a banner explaining the state. Fetched once on mount via
  // /api/feedback/mine. Cleared optimistically as soon as the user sends a
  // new message — the backend's ensureFeedbackRow flips the row back to
  // 'triaged' on that same message, so the local state stays aligned with
  // the server.
  const [ticketResolved, setTicketResolved] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/feedback/mine", {
          credentials: "include",
        });
        if (!res.ok) return;
        const body = (await res.json()) as MyTicketResponse;
        if (cancelled) return;
        setTicketResolved(body.ticket?.status === "resolved");
      } catch {
        // best-effort — if the fetch fails we just don't show the banner
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleConversationReady = useCallback(
    (
      conversationId: string,
      meta: { hasHumanMessages: boolean },
    ) => {
      // Re-opens of an existing feedback thread skip the enrichment POST so
      // the original report's debug_context isn't overwritten with stale
      // state from a later session. The backing row already exists (created
      // server-side on the first human message via ensureFeedbackRow), so
      // skipping here is safe.
      if (meta.hasHumanMessages) return;
      const debugContext = captureDebugContext(
        session,
        location.pathname,
        location.search,
      );
      void submitFeedbackReport(conversationId, debugContext);
    },
    [session, location.pathname, location.search],
  );

  const handleHumanMessageSent = useCallback(() => {
    // Backend auto-reopens resolved tickets on the next human message, so
    // the local banner is safe to clear immediately. If the send somehow
    // fails round-trip, the user will just see the reply flow normally
    // without a banner — which matches the actual server state.
    setTicketResolved(false);
  }, []);

  // Portaled to <body>: App renders this inside ShellLayout's <main>, whose
  // z-index:1 stacking context would trap the panel underneath the TopBar
  // (z-index:20) — burying the panel header and close button. The shell's
  // @yagni chat drawer escapes the same trap by being a sibling of <main>.
  return createPortal(
    <motion.div
      role="dialog"
      aria-label="Help & feedback"
      className="flex flex-col fixed inset-y-0 right-0 z-50 overflow-hidden"
      initial={reduceMotion ? { opacity: 0 } : { x: "100%" }}
      animate={reduceMotion ? { opacity: 1 } : { x: 0 }}
      transition={
        reduceMotion
          ? { duration: 0.12 }
          : { type: "spring", stiffness: 360, damping: 40, mass: 0.9 }
      }
      style={{
        // Same footprint family as the shell's @yagni chat drawer, one step
        // narrower so the two read as siblings rather than twins.
        width: "min(100vw, 480px)",
        minHeight: 0,
        borderLeft: "1px solid var(--rule)",
        background: "var(--paper-card)",
        boxShadow: "-24px 0 60px -34px rgba(45, 55, 95, 0.25)",
      }}
    >
      <ConversationSidebar
        tone="sidebar"
        title="Help & feedback"
        subtitle="Chat with @yagni. Jack from YAGNI reads every report."
        icon={
          <span
            className="flex h-5 w-5 items-center justify-center rounded-full"
            style={{
              background: "var(--accent-muted)",
              color: "var(--accent)",
            }}
          >
            <LifeBuoy size={12} strokeWidth={2.25} />
          </span>
        }
        actions={
          <IconButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close feedback panel"
            title="Close"
            className="text-[var(--text-tertiary)]"
          >
            <X size={16} />
          </IconButton>
        }
        banner={
          ticketResolved ? (
            <ConversationSidebarBanner
              tone="success"
              icon={<CheckCircle2 size={14} />}
              title="Marked as resolved"
              description="The YAGNI team closed this conversation. Send a new message to reopen it."
            />
          ) : null
        }
      >
        <div className="flex h-full min-h-0 flex-col">
          {/* Top-level self-serve escape hatch: guided lessons on the
              marketing site for people who want more help, not a ticket. */}
          <a
            href={ACADEMY_URL}
            target="_blank"
            rel="noreferrer"
            className="group flex shrink-0 items-center gap-2.5 px-5 py-2.5 text-[12.5px] transition-colors hover:bg-[rgba(var(--glass-rgb),0.5)]"
            style={{
              color: "var(--text-secondary)",
              borderBottom: "1px solid var(--border-subtle)",
            }}
          >
            <GraduationCap
              size={14}
              strokeWidth={2}
              style={{ color: "var(--accent)" }}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate">
              Just want more help?{" "}
              <span
                className="font-medium underline-offset-2 group-hover:underline"
                style={{ color: "var(--text-primary)" }}
              >
                Browse the YAGNI Academy
              </span>
            </span>
            <ArrowUpRight
              size={13}
              className="shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
              aria-hidden
            />
          </a>
          <div className="flex-1 min-h-0">
            <ConversationPanel
              parentType="feedback"
              parentId={session.user.id}
              name="Help & feedback"
              placeholder="Describe what happened, or ask for help…"
              onConversationReady={handleConversationReady}
              onHumanMessageSent={handleHumanMessageSent}
              chromeless
            />
          </div>
        </div>
      </ConversationSidebar>
    </motion.div>,
    document.body,
  );
}
